import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Link,
  VStack,
  Text,
  HStack,
  IconButton,
  Box,
  Divider,
  useColorModeValue,
  Badge,
  Wrap,
  WrapItem,
  Tooltip,
  Heading,
  Tag,
  TagLabel,
  TagLeftIcon
} from '@chakra-ui/react';
import { ArrowLeft, ArrowRight, Article, LinkSimpleHorizontal, Clock, Brain, TextT, CaretRight } from '@phosphor-icons/react';
import { mapFeature, normalizeFeature } from '../../utils/featureUtils';

// Format feature label for display
const formatFeatureLabel = (label) => {
  return String(label)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Helper function to safely format date
const formatDate = (dateValue) => {
  if (!dateValue) return 'Unknown date';
  
  try {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown date';
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    return 'Unknown date';
  }
};

export default function ArticleModal({
  isOpen,
  onClose,
  articleData,
  hasMultiple = false,
  onPrevious,
  onNext
}) {
  const iconColor = useColorModeValue('blue.500', 'blue.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const descBg = useColorModeValue('gray.50', 'gray.700');
  const featuresBg = useColorModeValue('gray.100', 'gray.700');
  const autoBadgeBg = useColorModeValue('purple.50', 'purple.900');
  const manualBadgeBg = useColorModeValue('blue.50', 'blue.900');
  const badgeColor = useColorModeValue('gray.800', 'white');
  const dateColor = useColorModeValue('gray.500', 'gray.500');

  if (!articleData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          borderBottomWidth="1px"
          borderColor={borderColor}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <HStack flex="1">
            <Article size={24} weight="duotone" color={iconColor} />
            <Text>Article</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={6}>
          <VStack spacing={5} align="stretch">
            {/* Title with link */}
            <VStack align="stretch" spacing={2}>
              <Link
                href={articleData.url}
                isExternal
                fontSize="xl"
                fontWeight="medium"
                color="blue.500"
                _hover={{ textDecoration: 'none', color: 'blue.600' }}
                display="flex"
                alignItems="center"
                gap={2}
              >
                {articleData.title}
                <LinkSimpleHorizontal size={16} weight="bold" />
              </Link>
              
              {articleData.source === 'gemini' && (
                <HStack spacing={2}>
                  <Tag
                    size="sm"
                    variant="subtle"
                    colorScheme="purple"
                  >
                    <TagLeftIcon as={Brain} />
                    <TagLabel>AI Sourced</TagLabel>
                  </Tag>
                  {articleData.groundingMetadata?.supportsCount > 0 && (
                    <Badge colorScheme="green">
                      {articleData.groundingMetadata.supportsCount} information segments
                    </Badge>
                  )}
                </HStack>
              )}
            </VStack>
            
            {/* Description */}
            {articleData.description && (
              <Box>
                <Heading size="sm" mb={2}>Description</Heading>
                <Box bg={descBg} p={4} borderRadius="md">
                  <Text>{articleData.description}</Text>
                </Box>
              </Box>
            )}

            {/* Grounding Supports - Show supported text segments */}
            {articleData.groundingMetadata?.supports?.length > 0 && (
              <Box>
                <Heading size="sm" mb={2}>Supported Information</Heading>
                <Box bg={descBg} p={4} borderRadius="md">
                  <VStack spacing={4} align="stretch">
                    {articleData.groundingMetadata.supports.map((support, idx) => (
                      <HStack key={idx} spacing={3} alignItems="flex-start">
                        <Box flexShrink={0} pt={1}>
                          <TextT size={16} weight="bold" color={iconColor} />
                        </Box>
                        <VStack align="stretch" spacing={2}>
                          <Text>{support.segment?.text}</Text>
                          {support.confidenceScores?.[0] && (
                            <Badge 
                              size="sm" 
                              colorScheme="green"
                              variant="subtle"
                            >
                              {Math.round(support.confidenceScores[0] * 100)}% confidence
                            </Badge>
                          )}
                        </VStack>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </Box>
            )}

            {/* Features */}
            {articleData.predictions?.length > 0 && (
              <Box>
                <Heading size="sm" mb={2}>Features</Heading>
                <Box bg={featuresBg} p={4} borderRadius="md">
                  <Wrap spacing={2}>
                    {articleData.predictions.map((pred, idx) => {
                      // Map the feature to get proper ID and label
                      const { featureId, label } = mapFeature(pred);
                      
                      return (
                        <WrapItem key={idx}>
                          <Tooltip 
                            label={`${pred.confidence ? `Confidence: ${Math.round(pred.confidence * 100)}% - ` : ''}${pred.source === 'manual' ? 'Manually tagged' : 'Auto-detected'}`} 
                            placement="top" 
                            hasArrow
                          >
                            <Badge 
                              px={2}
                              py={1}
                              borderRadius="full"
                              bg={pred.source === 'manual' ? manualBadgeBg : autoBadgeBg}
                              color={badgeColor}
                              cursor="pointer"
                              _hover={{
                                transform: 'translateY(-1px)',
                                boxShadow: 'sm'
                              }}
                              transition="all 0.2s"
                              onClick={() => {
                                // Close modal and bubble up event to parent
                                onClose();
                                // After slight delay to ensure modal is closed
                                setTimeout(() => {
                                  // Set the filter using the normalized feature ID
                                  const event = new CustomEvent('featureFilter', {
                                    detail: { featureId: normalizeFeature(featureId) }
                                  });
                                  document.dispatchEvent(event);
                                }, 100);
                              }}
                            >
                              {formatFeatureLabel(label || pred.class)}
                            </Badge>
                          </Tooltip>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              </Box>
            )}

            <Divider />

            {/* Upload date - De-emphasized */}
            <VStack align="stretch" spacing={2}>
              <HStack spacing={2} color={dateColor} fontSize="sm">
                <Clock size={14} />
                <Text>Added on {formatDate(articleData.uploadedAt)}</Text>
              </HStack>
              
              {articleData.source === 'gemini' && (
                <Text fontSize="sm" color="gray.500">
                  This source was automatically identified by Gemini AI to provide accurate information about this site.
                </Text>
              )}
            </VStack>

            {/* Navigation controls */}
            {hasMultiple && (
              <HStack justify="center" spacing={4} pt={2}>
                <IconButton
                  icon={<ArrowLeft weight="bold" />}
                  onClick={onPrevious}
                  aria-label="Previous article"
                  variant="ghost"
                />
                <IconButton
                  icon={<ArrowRight weight="bold" />}
                  onClick={onNext}
                  aria-label="Next article"
                  variant="ghost"
                />
              </HStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
